import { ErrorMessage } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import type { FlattenedSectionProps } from "../";
import { Question } from "../Question";
import type { QuestionProps } from "../Question/types";
import { isQuestion } from "../Question/types";
import {
  useAnswer,
  useQuestionnaire,
  useVisibilityMap,
} from "../../../../features/questionnaire";
import { useEnvironmentValues } from "../../../../hooks/environment/useEnvironment";
import { useLocale } from "../../../../hooks/useLocale";
import { prepareQuestionsForRender, type RenderItem, getErrors } from "./utils";
import { useCoverGenius } from "../../../../hooks/useCoverGenius";
import { isNoCoverValue } from "../Question/QuestionFactory/questions/PersonalisedCoverQuestion/constants";
import { useMemo } from "react";
import { Preamble, Postamble } from "../../Amble";
import "./PersonalisedCoverSection.css";

type QuestionItemProps = RenderItem & {
  errors?: Record<string, string>;
  visibilityMap?: Record<string, boolean>;
};
/**
 * Render a question item (primary + optional children) with error display
 */
const QuestionItem = ({
  primary,
  children,
  errors,
  visibilityMap,
}: QuestionItemProps) => {
  const { hasError, errorMessage } = getErrors({
    primary,
    children,
    errors,
    visibilityMap,
  });

  const answer = useAnswer(primary.name);
  const isActive = answer !== undefined && !isNoCoverValue(answer);

  return (
    <>
      <div
        data-testid="mobius-section__personalised-cover-group"
        className={classNames("mobius-section__personalised-cover-group", {
          "--has-children": children.length > 0,
          "--is-active": isActive,
          "--is-invalid": hasError,
        })}
      >
        <Question {...primary} />
        {children.map(child => (
          <Question key={child.name} {...child} />
        ))}
      </div>
      {hasError && <ErrorMessage errorMessage={errorMessage} />}
    </>
  );
};

export const PersonalisedCoverSection = ({
  ref,
  ...props
}: FlattenedSectionProps) => {
  const { title, preamble, postamble, questions, className } = props;
  const classes = classNames("mobius", "mobius-section", className);
  const { groups = {}, errors = {} } = useQuestionnaire();
  const visibilityMap = useVisibilityMap();
  const { site } = useEnvironmentValues();
  const { locale } = useLocale();

  const primaryQuestions: QuestionProps[] = useMemo(
    () => questions.filter(q => q.display?.type === "personalised_cover"),
    [questions],
  );
  useCoverGenius({ questions: primaryQuestions });

  return (
    <section data-testid="mobius-section" ref={ref} className={classes}>
      {title && <h2 className="mobius-section__title">{title}</h2>}
      <Preamble amble={preamble} site={site} locale={locale} />
      <div className="mobius-section__question-container">
        {prepareQuestionsForRender({
          questions: questions.filter(isQuestion),
          groups,
          visibilityMap,
        }).map(item => (
          <QuestionItem
            key={item.primary.id}
            {...item}
            errors={errors}
            visibilityMap={visibilityMap}
          />
        ))}
      </div>
      <Postamble amble={postamble} site={site} locale={locale} />
    </section>
  );
};

PersonalisedCoverSection.displayName = "PersonalisedCoverSection";
